import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Package } from "lucide-react";

/**
 * Procurement Overview (Feb 2026) — CRM Manager / CRM Executive / Director.
 * Reads `/api/procurement/summary` (per-project received-vs-total roll-up) and
 * renders a searchable, sortable list so CRM can see fulfilment progress
 * without leaving their workspace.
 */
export default function ProcurementOverviewPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/procurement/summary");
      setRows(data.rows || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    let arr = rows;
    if (s) {
      arr = arr.filter((r) => [r.project_name, r.client_name]
        .filter(Boolean).some((v) => v.toLowerCase().includes(s)));
    }
    // Sort: least fulfilled first (so bottlenecks bubble up)
    return [...arr].sort((a, b) => (a.fulfillment_pct || 0) - (b.fulfillment_pct || 0));
  }, [rows, q]);

  const totals = useMemo(() => {
    const total = rows.reduce((n, r) => n + (r.items_total || 0), 0);
    const received = rows.reduce((n, r) => n + (r.items_received || 0), 0);
    return { total, received, pct: total ? Math.round((received / total) * 100) : 0 };
  }, [rows]);

  return (
    <div className="min-h-full">
      <div className="border-b border-[#262626] px-6 md:px-10 py-6 bg-[#050505]/80 sticky top-0 z-30">
        <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">CRM · Read-Only</p>
        <h1 className="font-display text-2xl md:text-3xl text-white mt-1">Procurement Overview</h1>
        <p className="text-[11px] text-gray-500 mt-1 italic">
          Received vs total per project, across every order in Sourcing & Purchasing.
        </p>
      </div>
      <div className="px-6 md:px-10 py-6 space-y-4">
        {/* Aggregate */}
        <div className="iz-card p-4 flex items-center gap-4 flex-wrap" data-testid="proc-overview-totals">
          <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">Portfolio</p>
          <p className="font-display text-2xl text-white">
            {totals.received}<span className="text-gray-500 text-lg"> / {totals.total}</span>
          </p>
          <div className="flex-1 max-w-[300px] h-1.5 bg-[#111] rounded overflow-hidden">
            <div className="h-full bg-emerald-400" style={{ width: `${totals.pct}%` }} />
          </div>
          <p className="text-xs font-mono text-gray-400">{totals.pct}% fulfilled across {rows.length} project(s)</p>
        </div>

        <input value={q} onChange={(e) => setQ(e.target.value)}
               placeholder="Search by project or client…"
               className="iz-input w-full text-xs"
               data-testid="proc-overview-search"/>

        {loading ? (
          <div className="iz-card p-6 flex items-center gap-2 text-xs text-gray-500">
            <Loader2 className="w-4 h-4 animate-spin"/> Loading…
          </div>
        ) : filtered.length === 0 ? (
          <div className="iz-card p-10 text-center text-sm text-gray-500 italic">
            {rows.length === 0 ? "No procurement orders yet." : `Nothing matches "${q}".`}
          </div>
        ) : (
          <div className="iz-card overflow-hidden">
            <table className="w-full text-sm" data-testid="proc-overview-table">
              <thead>
                <tr className="text-[9px] font-mono uppercase tracking-widest text-gray-500 border-b border-[#1a1a1a] bg-[#080808]">
                  <th className="text-left px-3 py-2.5">Client · Project</th>
                  <th className="text-right px-3 py-2.5">Received</th>
                  <th className="text-right px-3 py-2.5">Total</th>
                  <th className="text-left px-3 py-2.5 w-[220px]">Fulfilment</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.order_id} className="border-b border-[#0f0f0f] hover:bg-[#0a0a0a]"
                      data-testid={`proc-overview-row-${r.order_id}`}>
                    <td className="px-3 py-2.5 text-white">
                      <div className="truncate max-w-[360px]">{r.client_name || "—"}</div>
                      <div className="text-[10px] text-gray-500 truncate max-w-[360px]">{r.project_name || "—"}</div>
                    </td>
                    <td className="px-3 py-2.5 text-right text-emerald-300 font-mono">{r.items_received}</td>
                    <td className="px-3 py-2.5 text-right text-white font-mono">{r.items_total}</td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-1.5 bg-[#111] rounded overflow-hidden max-w-[140px]">
                          <div className="h-full bg-emerald-400"
                               style={{ width: `${r.fulfillment_pct}%` }} />
                        </div>
                        <span className="font-mono text-[10px] text-gray-400">{r.fulfillment_pct}%</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
